import React, { useContext, memo, useEffect, useState, useRef, useMemo } from "react";
import axios from "axios";
import {
  Polygon
} from "@react-google-maps/api";
import tinygradient from "tinygradient";
import { WebMapContext } from "../WebMapGoogleMap";
import { DemandScoreTypes, SEARCH_AREA_STATES, demandLegendGradient, demandScoreMapper, demandTooltipImg, demandTooltipTitle } from "../../../../constants/WebMap";
import { convertArrayToStringWithDelimeter, convertGeohashToPolygon } from "../../../../utils/helper";
import { getUniqueGeohashes, paginatedApiInvoker } from "../../../../utils/webMapApiHelper";
import { getDemandFinalScoreGeohashes, getDemandOperatorGeohashes, getDemandTooltipData } from "../../../../services/api/mapGeohashes.api";

const gradient = tinygradient(demandLegendGradient);

const GeohashesOnMap = ({ geohashes = [], demandFilters, mapTool, onClick }) => (
  <div className="data-on-map">
    {geohashes?.map((data, index) => {
      const { demandType, range } = demandFilters;
      const demandScore = data[demandScoreMapper[demandType]];
      let color = "grey";
      if(demandType === DemandScoreTypes.AFFLUENCE){
        color = demandFilters?.strengthLevels[demandScore] || demandFilters?.strengthLevels['No Data'];
      } else if (demandScore) {
        color = gradient.rgbAt(Number(demandScore));
      }
        
      if (
        (demandScore &&
          Number(demandScore) >= range[0] &&
          Number(demandScore) <= range[1]) ||
        (!demandScore && !demandFilters.noData) || demandType === DemandScoreTypes.AFFLUENCE
      )
        return (
          <Polygon
            path={convertGeohashToPolygon(data.geohash)}
            key={index}
            options={{
              fillColor: color,
              fillOpacity: 0.8,
              strokeColor: "#bdbdbd",
              strokeOpacity: 0.8,
              strokeWeight: 2,
              clickable: !mapTool && demandType !== DemandScoreTypes.AFFLUENCE,
              draggable: false,
              editable: false,
              geodesic: false,
              zIndex: 1,
            }}
            onClick={() => {
              onClick(data);
            }}
          />
        );
    })}
  </div>
);

const DemandGeohashSection = ({
  mapTool,
  preferedGeohashZoom,
  setTooltipData,
  tooltipData,
  searchAreaBtn,
  setSearchAreaBtn,
  setApisOngoing,
  saveLastBound,
  operatorsPermittedDemand
}) => {
  let cancelToken;
  let timeOutForApiCall = 600;
  const demandFSTO = useRef();
  const demandOperatorTO = useRef();

  const {
    eyeIcons,
    demandFilters,
    mapZoomLevel,
    geohashZoom,
    geohashNE,
    geohashSW,
    appliedFilters,
    boundsChanged,
    getBbox,
    operatorDetails,
    downloadData,
    setDownloadData,
    affluenceLvlFilters,
  } = useContext(WebMapContext);
  const operatorRef = useRef(demandFilters.operator);

  const [demandFSGeohashes, setDemandFSGeohashes] = useState([]);
  const [demandOptrGeohashes, setDemandOptrGeohashes] = useState([]);

  useEffect(() => {
    if ((demandFilters.demandType === DemandScoreTypes.FINAL &&
      mapZoomLevel < preferedGeohashZoom) ||
      (geohashZoom && mapZoomLevel < geohashZoom))
      resetData();
  }, [mapZoomLevel, geohashZoom, demandFilters.demandType]);

  useEffect(() => {
    resetData();
  }, [demandFilters.demandType]);

  useEffect(() => {
    if (!eyeIcons.demand) resetData();
  }, [eyeIcons.demand]);

  useEffect(() => {
    operatorRef.current = demandFilters.operator;
  }, [demandFilters.operator]);

  useEffect(() => {
    if (common_cond_fs) {
      fetchDemandFSGeohashesVP();
      saveLastBound(); // save data loaded bounds
    }
    // eslint-disable-next-line
  }, [
    geohashSW,
    eyeIcons.demand,
    appliedFilters.level,
    appliedFilters.levelVal,
    demandFilters.demandType,
    boundsChanged,
    appliedFilters.year,
    appliedFilters.quarter
  ]);

  useEffect(() => {
    if (common_cond_fs && searchAreaBtn === SEARCH_AREA_STATES.FETCH) {
      fetchDemandFSGeohashesVP();
      saveLastBound(); // save data loaded bounds
    }
    // eslint-disable-next-line
  }, [searchAreaBtn]);

  useEffect(() => {
    if (common_cond_optr) {
      setTimeout(() => {
        fetchDemandOperatorGeohashesVP();
      }, 1000)
      saveLastBound(); // save data loaded bounds
    }
    // eslint-disable-next-line
  }, [
    geohashSW,
    eyeIcons.demand,
    appliedFilters.level,
    appliedFilters.levelVal,
    demandFilters.demandType,
    demandFilters.operator,
    demandFilters.connectionType,
    demandFilters.dataType,
    affluenceLvlFilters,
    geohashZoom,
    boundsChanged,
    appliedFilters.year,
    appliedFilters.quarter
  ]);

  useEffect(() => {
    if (common_cond_optr && searchAreaBtn === SEARCH_AREA_STATES.FETCH) {
      fetchDemandOperatorGeohashesVP();
      saveLastBound(); // save data loaded bounds
    }
    // eslint-disable-next-line
  }, [searchAreaBtn]);

  const common_cond_fs = useMemo(() => {
    return eyeIcons.demand &&
      demandFilters.demandType === DemandScoreTypes.FINAL &&
      mapZoomLevel >= preferedGeohashZoom;
  }, [eyeIcons.demand, demandFilters.demandType, mapZoomLevel]);

  const common_cond_optr = useMemo(() => {
    return (
      eyeIcons.demand &&
      geohashZoom &&
      mapZoomLevel >= geohashZoom &&
      ((demandFilters.demandType !== DemandScoreTypes.FINAL &&
        demandFilters.connectionType &&
        demandFilters.dataType) ||
        (demandFilters.demandType === DemandScoreTypes.AFFLUENCE &&
          affluenceLvlFilters?.length))
    );
  }, [eyeIcons.demand, demandFilters, geohashZoom, mapZoomLevel, affluenceLvlFilters]);

  const resetData = () => {
    setDemandFSGeohashes([]);
    setDemandOptrGeohashes([]);
  }

  const fetchDemandFSGeohashesVP = async () => {
    resetData();
    if (typeof cancelToken != typeof undefined) {
      cancelToken.cancel("Operation canceled due to new request.");
    }
    //Save the cancel token for the current request
    cancelToken = axios.CancelToken.source();
    clearTimeout(demandFSTO.current);

    demandFSTO.current = setTimeout(async () => {
      try {
        const { neBound, swBound } = getBbox;

        const payload = {
          ...appliedFilters,
          level: appliedFilters.level !== 'geoHashLevel' ? appliedFilters.level : null,
          levelVal: appliedFilters.level !== 'geoHashLevel' ? appliedFilters.levelVal : null,
          northEast: convertArrayToStringWithDelimeter(geohashNE.length ? geohashNE : neBound, "_"),
          southWest: convertArrayToStringWithDelimeter(geohashSW.length ? geohashSW : swBound, "_")
        }

        await paginatedApiInvoker(
          (pageSize, page) => getDemandFinalScoreGeohashes(payload, cancelToken, pageSize, page),
          30000, // perPage
          () => setApisOngoing(count => count + 1),
          (data) => {
            if (data) setDemandFSGeohashes(prevState => [...getUniqueGeohashes([...prevState, ...data])]);
          },
          () => {
            //Saving filters for download
            setDownloadData({
              ...downloadData,
              demand: {
                function: getDemandFinalScoreGeohashes,
                ...payload,
                cancelToken: cancelToken,
              },
            });
            if (!appliedFilters.level) setSearchAreaBtn(SEARCH_AREA_STATES.ENABLED);
            setApisOngoing(count => count - 1);
          }
        );
      } catch (err) {
        console.log(err);
      }
    }, timeOutForApiCall);
  };

  const fetchDemandOperatorGeohashesVP = async () => {
    resetData();
    if (typeof cancelToken != typeof undefined) {
      cancelToken.cancel("Operation canceled due to new request.");
    }
    //Save the cancel token for the current request
    cancelToken = axios.CancelToken.source();
    clearTimeout(demandOperatorTO.current);

    // Per Edotco team's request: when demandType is AFFLUENCE, set 'dt' to 'Macro'
    demandOperatorTO.current = setTimeout(async () => {
      try {
        const { neBound, swBound } = getBbox;
        
        const payload = {
          ...appliedFilters,
          level: appliedFilters.level !== 'geoHashLevel' ? appliedFilters.level : null,
          levelVal: appliedFilters.level !== 'geoHashLevel' ? appliedFilters.levelVal : null,
          operator: operatorRef.current,
          ct: demandFilters.connectionType,
          dt: !(demandFilters.demandType === DemandScoreTypes.AFFLUENCE) ? demandFilters.dataType : 'Macro',
          ...(demandFilters.demandType === DemandScoreTypes.AFFLUENCE && affluenceLvlFilters?.length && { filter: convertArrayToStringWithDelimeter(affluenceLvlFilters.map(({ key, isChecked }) => isChecked ? key : null).filter((item) => item), "_") }),
          northEast: convertArrayToStringWithDelimeter(geohashNE.length ? geohashNE : neBound, "_"),
          southWest: convertArrayToStringWithDelimeter(geohashSW.length ? geohashSW : swBound, "_")
        }

        await paginatedApiInvoker(
          (pageSize, page) => getDemandOperatorGeohashes(payload, cancelToken, pageSize, page),
          100000, // perPage
          () => setApisOngoing(count => count + 1),
          (data) => {
            if (data) setDemandOptrGeohashes(prevState => [...getUniqueGeohashes([...prevState, ...data])]);
          },
          () => {
            //Saving filters for download
            setDownloadData({
              ...downloadData,
              demand: {
                function: getDemandOperatorGeohashes,
                ...payload,
                cancelToken: cancelToken,
              },
            });
            if (!appliedFilters.level) setSearchAreaBtn(SEARCH_AREA_STATES.ENABLED);
            setApisOngoing(count => count - 1);
          }
        );
      } catch (err) {
        console.log(err);
      }
    }, timeOutForApiCall);
  };

  const onClickDemandGeohash = async (params) => {
    if (!tooltipData || tooltipData?.geohash !== params.geohash) {
      let data;
      try {
        const { data: res } = await getDemandTooltipData({
          ...appliedFilters,
          geohash: params.geohash,
        });
        data = res;
      } catch (err) {
        console.log("Erro while getting tooltip data : ", err);
      }
      const { demandType } = demandFilters;
      const demandScore = params[demandScoreMapper[demandType]];
      const geohashColor = demandScore
        ? gradient.rgbAt(Number(demandScore))
        : "grey";
      const polygon = convertGeohashToPolygon(params.geohash);
      const selectedOp = operatorsPermittedDemand.find(
        (x) => x.operator === demandFilters.operator);
        const header  = {
            logo: selectedOp?.icon,
            name: selectedOp?.name,
            isAnonymized: selectedOp?.isAnonymized
          } 
      const tooltip = {
        ...params,
        polygon,
        header: {
          ...(demandType === DemandScoreTypes.FINAL
            ? {
              img: demandTooltipImg[demandType],
              title: demandTooltipTitle[demandType],
              colorBox: geohashColor,
              value: demandScore || "No Data",
            }
            : header
            ),
        },
        body: []
      };
      if (data) {
        tooltip.body = [{
          img: "population",
          text: "Population",
          value: data.pop || "No Data",
        },
        {
          img: "totalPOI",
          text: "Total POI",
          value: data.total_poi || "N/A",
        },
        {
          img: "roadcount",
          text: "Road Count",
          value: data.road_count || "N/A",
        },
        {
          img: "roadlength",
          text: "Road Length",
          value: data.road_length ? `${data.road_length} m ` : "N/A",
        },
        {
          img: "devicecount",
          text: "Device Count",
          value: data.dev_count || "N/A",
        }]
      }
      if (demandType !== DemandScoreTypes.FINAL)
        tooltip.body.unshift({
          img: demandTooltipImg[demandType],
          text: demandTooltipTitle[demandType],
          value: demandScore || "N/A",
          bold: true,
          colorBox: geohashColor,
        });
      setTooltipData(tooltip);
    }
  };

  return (
    mapZoomLevel >= geohashZoom && eyeIcons.demand
      ? demandFilters.demandType === DemandScoreTypes.FINAL && demandFSGeohashes.length > 0
        ? <GeohashesOnMap
          geohashes={demandFSGeohashes}
          mapTool={mapTool}
          demandFilters={demandFilters}
          onClick={onClickDemandGeohash}
        />
        : demandFilters.demandType !== DemandScoreTypes.FINAL && demandOptrGeohashes.length > 0
          ? <GeohashesOnMap
            geohashes={demandOptrGeohashes}
            mapTool={mapTool}
            demandFilters={demandFilters}
            onClick={onClickDemandGeohash}
          />
          : <></>
      : <></>
  )
};

export default memo(DemandGeohashSection);